import { errorOut } from '../services/datadog'
import type { AppStore } from '../store'

import { journeyIdFromUrl } from './journeyIdFromUrl'
import { readMarker } from './marker'
import { isOrdersUrl } from './ordersUrl'
import type { BootView, MarkerEntry } from './types'

type AppState = ReturnType<AppStore['getState']>

type Loader = () => Promise<{ activateOffline(): Promise<unknown> }>

type RouteLoader = () => Promise<{
  routeOfflineBoot(args: {
    href: string
    journeyId: string
    marker?: MarkerEntry
  }): Promise<BootView>
}>

type Options = {
  href?: string
  storage?: Pick<Storage, 'getItem'>
  store?: AppStore
  load?: Loader
  /** The browser's own verdict; boot routing runs only when it says offline (spec §12.11). */
  isOffline?: () => boolean
  route?: RouteLoader
}

/** Upper bound on how long the first render waits for each offline boot step (routing, activation). */
const BOOT_STEP_TIMEOUT_MS = 3_000

const JOURNEY: BootView = { view: 'journey' }

const TIMED_OUT = Symbol('timed out')

/** `localStorage` itself throws (SecurityError) in storage-blocked embeds. */
const safeLocalStorage = (): Pick<Storage, 'getItem'> | null => {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

const browserIsOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false

const fail = (message: string) => (originalError: unknown) => {
  const error = new Error(message)

  error.cause = originalError
  errorOut(error)
}

/** `task`, or TIMED_OUT once the first render waited long enough (the task keeps running). */
const withinBootBudget = async <T>(
  task: Promise<T>,
  what: string
): Promise<T | typeof TIMED_OUT> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timedOut = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), BOOT_STEP_TIMEOUT_MS)
  })
  const winner = await Promise.race([task, timedOut])

  clearTimeout(timer)

  if (winner === TIMED_OUT) {
    errorOut(
      new Error(
        `Offline add-on: ${what} took over ${BOOT_STEP_TIMEOUT_MS}ms, rendering without it`
      )
    )
  }

  return winner
}

/**
 * Decides what the page renders and whether the offline add-on must be loaded, and when.
 *
 * - The Orders URL (`?offline=orders`, Phase 2 §7.1): the Orders page, online or offline. It reads
 *   the device's database itself; no journey, no add-on.
 * - Offline (the browser says so), a journey link: a lazily loaded routing decides whether this
 *   device holds the order; an unprepared context journey renders the Orders page with the reason.
 * - Journey prepared on this device (marker): load BEFORE the first request,
 *   otherwise the config fetch fails offline before any interceptor exists.
 * - Otherwise: wait for the config, and load if the org flag is on AND the
 *   served revision opted in (`settings.offline.enabled`, see `gate.ts`).
 *
 * Never throws: a broken add-on must leave the journey app as it is today.
 *
 * This module is in the entry chunk, so the store, its selectors, the routing and the Orders page
 * are only imported dynamically (a static import here would grow the entry bundle of every journey).
 */
export const bootstrapOffline = async ({
  href = window.location.href,
  storage,
  store,
  load = () => import('./index'),
  isOffline = browserIsOffline,
  route = () => import('./orders/route')
}: Options = {}): Promise<BootView> => {
  if (isOrdersUrl(href)) return { view: 'orders' }

  const journeyId = journeyIdFromUrl(href)

  if (!journeyId) return JOURNEY

  const activate = async () => {
    try {
      const addon = await load()

      await addon.activateOffline()
    } catch (originalError) {
      fail('Offline add-on failed to load')(originalError)
    }
  }

  const source = storage ?? safeLocalStorage()
  const marker = source ? readMarker(source) : {}

  if (isOffline()) {
    const decided = await withinBootBudget(
      route()
        .then(({ routeOfflineBoot }) =>
          routeOfflineBoot({ href, journeyId, marker: marker[journeyId] })
        )
        .catch((originalError: unknown) => {
          fail('Offline add-on: offline routing failed')(originalError)

          return JOURNEY
        }),
      'offline routing'
    )

    if (decided !== TIMED_OUT && decided.view === 'orders') return decided
  }

  if (marker[journeyId]) {
    // Activation keeps going in the background if it loses the race.
    await withinBootBudget(activate(), 'activation')

    return JOURNEY
  }

  // Not awaited: the render must not wait for the store chunk.
  void Promise.all([
    store ?? import('../store').then((module) => module.appStore),
    import('../store/selectors/config'),
    import('./gate')
  ])
    .then(([target, { selectHydratedJourneyId }, { isOfflineOn }]) => {
      const onState = (state: AppState) => {
        if (!selectHydratedJourneyId(state)) return

        unsubscribe()

        if (isOfflineOn(state)) void activate()
      }
      const unsubscribe = target.subscribe(onState)

      // Hydration may have happened while the store chunk was loading.
      onState(target.getState())
    })
    .catch(fail('Offline add-on: store unavailable'))

  return JOURNEY
}
